import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  BookOpen,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderTree,
  Layers3,
  LineChart,
  Loader2,
  RefreshCw,
  ShoppingBag,
  TrendingUp,
  UtensilsCrossed,
} from 'lucide-react';
import {
  analyticsApi,
  menusApi,
} from '../../api';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import '../../styles/Analytics/menu-analytics.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const ESTABLISHMENT_STORAGE_KEY = 'selected_establishment_id';
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const ALL_MENUS_VALUE = 'all';
const ALL_TREND_ITEMS_VALUE = 'all';
const TREND_SCOPE_PRODUCTS = 'products';
const TREND_SCOPE_CATEGORIES = 'categories';

const TREND_COLORS = [
  '#51766e',
  '#c46a1a',
  '#5478a8',
  '#8b68a8',
  '#4f946d',
  '#b85e72',
  '#8b7a46',
  '#3d8791',
];

const DATE_PICKER_MIN_YEAR = 1990;
const DATE_PICKER_MAX_YEAR =
  new Date().getFullYear() + 10;

const formatNumber = (value, language) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};

const formatAmount = (
  value,
  currency = 'MAD',
  language,
) => {
  const amount = Number(value || 0);
  const safeCurrency = currency || 'MAD';

  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${safeCurrency}`;
  }
};

const translateFallback = (t, key, fallback) => {
  const value = t(key);

  return !value || value === key
    ? fallback
    : value;
};



const padNumber = (value) =>
  String(value).padStart(2, '0');

const toDateValue = (date) =>
  `${date.getFullYear()}-${padNumber(
    date.getMonth() + 1,
  )}-${padNumber(date.getDate())}`;

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value
    .split('-')
    .map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(
    year,
    month - 1,
    day,
  );
};

function CustomSelect({
  value,
  options = [],
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  className = '',
}) {
  const rootRef =
    useRef(null);

  const [open, setOpen] =
    useState(false);

  useEffect(() => {
    const handleOutside = (
      event,
    ) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutside,
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        handleOutside,
      );
  }, []);

  const selected =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  return (
    <div
      ref={rootRef}
      className={`menu-analytics-custom-select ${className}`}
    >
      <button
        type="button"
        className={`menu-analytics-custom-select__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() =>
          !disabled &&
          setOpen(
            (current) =>
              !current,
          )
        }
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span>
          {selected?.label ||
            placeholder ||
            '—'}
        </span>

        <ChevronDown
          size={16}
          className={
            open
              ? 'is-rotated'
              : ''
          }
        />
      </button>

      {open ? (
        <div
          className="menu-analytics-custom-select__menu"
          role="listbox"
        >
          {options.map(
            (option) => {
              const active =
                String(
                  option.value,
                ) ===
                String(value);

              return (
                <button
                  key={
                    option.value
                  }
                  type="button"
                  role="option"
                  aria-selected={
                    active
                  }
                  className={
                    active
                      ? 'is-active'
                      : ''
                  }
                  onClick={() => {
                    onChange?.(
                      option.value,
                    );
                    setOpen(false);
                  }}
                >
                  <span>
                    {option.label}
                  </span>

                  {active ? (
                    <Check
                      size={15}
                    />
                  ) : null}
                </button>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}

function CustomDatePicker({
  value,
  onChange,
  label,
  placeholder,
  min,
  max,
  t,
  language,
}) {
  const rootRef =
    useRef(null);

  const parsedValue =
    parseDateValue(value);

  const [open, setOpen] =
    useState(false);

  const [
    visibleMonth,
    setVisibleMonth,
  ] = useState(
    parsedValue ||
      new Date(),
  );

  useEffect(() => {
    if (parsedValue) {
      setVisibleMonth(
        parsedValue,
      );
    }
  }, [value]);

  useEffect(() => {
    const handleOutside = (
      event,
    ) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutside,
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        handleOutside,
      );
  }, []);

  const year =
    visibleMonth.getFullYear();

  const month =
    visibleMonth.getMonth();

  const monthStart =
    new Date(
      year,
      month,
      1,
    );

  const mondayOffset =
    (monthStart.getDay() +
      6) %
    7;

  const gridStart =
    new Date(
      year,
      month,
      1 - mondayOffset,
    );

  const days =
    Array.from(
      { length: 42 },
      (_, index) => {
        const day =
          new Date(
            gridStart,
          );

        day.setDate(
          gridStart.getDate() +
            index,
        );

        return day;
      },
    );

  const minDate =
    parseDateValue(min);

  const maxDate =
    parseDateValue(max);

  const isDisabled =
    (day) => {
      const time =
        new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
        ).getTime();

      if (
        minDate &&
        time <
          new Date(
            minDate.getFullYear(),
            minDate.getMonth(),
            minDate.getDate(),
          ).getTime()
      ) {
        return true;
      }

      if (
        maxDate &&
        time >
          new Date(
            maxDate.getFullYear(),
            maxDate.getMonth(),
            maxDate.getDate(),
          ).getTime()
      ) {
        return true;
      }

      return false;
    };

  const formatDisplay =
    (date) => {
      if (!date) {
        return '';
      }

      try {
        return new Intl.DateTimeFormat(
          language,
          {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          },
        ).format(date);
      } catch {
        return toDateValue(
          date,
        );
      }
    };

  const monthOptions =
    Array.from(
      { length: 12 },
      (_, monthIndex) => ({
        value: monthIndex,
        label:
          new Intl.DateTimeFormat(
            language,
            {
              month: 'long',
            },
          ).format(
            new Date(
              2026,
              monthIndex,
              1,
            ),
          ),
      }),
    );

  const yearOptions =
    Array.from(
      {
        length:
          DATE_PICKER_MAX_YEAR -
          DATE_PICKER_MIN_YEAR +
          1,
      },
      (_, index) =>
        DATE_PICKER_MIN_YEAR +
        index,
    );

  const weekdays =
    Array.from(
      { length: 7 },
      (_, index) => {
        const date =
          new Date(
            2026,
            0,
            5 + index,
          );

        return new Intl.DateTimeFormat(
          language,
          {
            weekday:
              'short',
          },
        ).format(date);
      },
    );

  const selectedValue =
    parsedValue
      ? toDateValue(
          parsedValue,
        )
      : '';

  return (
    <div
      ref={rootRef}
      className="menu-analytics-date-picker"
    >
      <span className="menu-analytics-date-picker__label">
        {label}
      </span>

      <button
        type="button"
        className={`menu-analytics-date-picker__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() =>
          setOpen(
            (current) =>
              !current,
          )
        }
      >
        <CalendarDays
          size={17}
        />

        <span
          className={
            parsedValue
              ? ''
              : 'is-placeholder'
          }
        >
          {parsedValue
            ? formatDisplay(
                parsedValue,
              )
            : placeholder}
        </span>

        <ChevronDown
          size={16}
          className={
            open
              ? 'is-rotated'
              : ''
          }
        />
      </button>

      {open ? (
        <div className="menu-analytics-date-picker__popover">
          <div className="menu-analytics-date-picker__header">
            <button
              type="button"
              className="menu-analytics-date-picker__nav"
              onClick={() =>
                setVisibleMonth(
                  new Date(
                    year,
                    month - 1,
                    1,
                  ),
                )
              }
              aria-label={translateFallback(
                t,
                'common.previous',
                'Previous',
              )}
            >
              <ChevronLeft
                size={17}
              />
            </button>

            <div className="menu-analytics-date-picker__jump">
              <CustomSelect
                value={
                  String(month)
                }
                onChange={(
                  nextMonth,
                ) =>
                  setVisibleMonth(
                    new Date(
                      year,
                      Number(
                        nextMonth,
                      ),
                      1,
                    ),
                  )
                }
                ariaLabel={translateFallback(
                  t,
                  'menuAnalytics.filters.month',
                  'Month',
                )}
                className="menu-analytics-date-picker__month-select"
                options={monthOptions.map(
                  (item) => ({
                    value:
                      String(
                        item.value,
                      ),
                    label:
                      item.label,
                  }),
                )}
              />

              <CustomSelect
                value={
                  String(year)
                }
                onChange={(
                  nextYear,
                ) =>
                  setVisibleMonth(
                    new Date(
                      Number(
                        nextYear,
                      ),
                      month,
                      1,
                    ),
                  )
                }
                ariaLabel={translateFallback(
                  t,
                  'menuAnalytics.filters.year',
                  'Year',
                )}
                className="menu-analytics-date-picker__year-select"
                options={yearOptions.map(
                  (item) => ({
                    value:
                      String(
                        item,
                      ),
                    label:
                      formatNumber(
                        item,
                        language,
                      ),
                  }),
                )}
              />
            </div>

            <button
              type="button"
              className="menu-analytics-date-picker__nav"
              onClick={() =>
                setVisibleMonth(
                  new Date(
                    year,
                    month + 1,
                    1,
                  ),
                )
              }
              aria-label={translateFallback(
                t,
                'common.next',
                'Next',
              )}
            >
              <ChevronRight
                size={17}
              />
            </button>
          </div>

          <div className="menu-analytics-date-picker__weekdays">
            {weekdays.map(
              (
                weekday,
                index,
              ) => (
                <span
                  key={`${weekday}-${index}`}
                >
                  {weekday}
                </span>
              ),
            )}
          </div>

          <div className="menu-analytics-date-picker__grid">
            {days.map(
              (day) => {
                const dayValue =
                  toDateValue(
                    day,
                  );

                const outside =
                  day.getMonth() !==
                  month;

                const selected =
                  dayValue ===
                  selectedValue;

                const disabled =
                  isDisabled(
                    day,
                  );

                return (
                  <button
                    key={dayValue}
                    type="button"
                    disabled={
                      disabled
                    }
                    className={[
                      outside
                        ? 'is-outside'
                        : '',
                      selected
                        ? 'is-selected'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      onChange?.(
                        dayValue,
                      );
                      setOpen(false);
                    }}
                  >
                    {formatNumber(
                      day.getDate(),
                      language,
                    )}
                  </button>
                );
              },
            )}
          </div>

          <div className="menu-analytics-date-picker__footer">
            <button
              type="button"
              onClick={() => {
                onChange?.('');
                setOpen(false);
              }}
            >
              {translateFallback(
                t,
                'common.clear',
                'Clear',
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                const today =
                  new Date();

                const todayValue =
                  toDateValue(
                    today,
                  );

                if (
                  !isDisabled(
                    today,
                  )
                ) {
                  onChange?.(
                    todayValue,
                  );
                  setVisibleMonth(
                    today,
                  );
                  setOpen(false);
                }
              }}
            >
              {translateFallback(
                t,
                'menuAnalytics.filters.today',
                'Today',
              )}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const buildSmoothPath = (points) => {
  if (!points.length) {
    return '';
  }

  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y}`;
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (
    let index = 0;
    index < points.length - 1;
    index += 1
  ) {
    const current = points[index];
    const next = points[index + 1];
    const middleX = (current.x + next.x) / 2;

    path += ` C ${middleX} ${current.y}, ${middleX} ${next.y}, ${next.x} ${next.y}`;
  }

  return path;
};

function MultiSeriesTrendChart({
  series = [],
  selectedId = ALL_TREND_ITEMS_VALUE,
  language,
  label,
  allLabel,
}) {
  const visibleSeries = useMemo(() => {
    if (
      selectedId !==
      ALL_TREND_ITEMS_VALUE
    ) {
      return series.filter(
        (item) =>
          String(item.id) ===
          String(selectedId),
      );
    }

    /*
     * Keep the default "all" graph readable.
     * The backend returns series ordered by total views,
     * so the four strongest series are displayed together.
     * Every product/category remains available in the select.
     */
    return series.slice(0, 4);
  }, [
    selectedId,
    series,
  ]);

  const width = 860;
  const height = 330;

  const isRtl = String(language || '')
    .toLowerCase()
    .startsWith('ar');

  const padding = isRtl
    ? {
        top: 28,
        right: 52,
        bottom: 50,
        left: 24,
      }
    : {
        top: 28,
        right: 24,
        bottom: 50,
        left: 52,
      };

  const chartWidth =
    width -
    padding.left -
    padding.right;

  const chartHeight =
    height -
    padding.top -
    padding.bottom;

  const pointCount =
    visibleSeries[0]
      ?.points
      ?.length || 0;

  const maxValue = Math.max(
    1,
    ...visibleSeries.flatMap(
      (item) =>
        (item.points || []).map(
          (point) =>
            Number(
              point.value || 0,
            ),
        ),
    ),
  );

  const yMax =
    maxValue +
    Math.max(
      1,
      maxValue * 0.12,
    );

  const preparedSeries =
    visibleSeries.map(
      (item, seriesIndex) => ({
        ...item,

        color:
          TREND_COLORS[
            seriesIndex %
            TREND_COLORS.length
          ],

        coords:
          (item.points || []).map(
            (
              point,
              index,
            ) => {
              const ratio =
                pointCount <= 1
                  ? 0.5
                  : index /
                    (pointCount - 1);

              const x =
                pointCount <= 1
                  ? padding.left +
                    chartWidth / 2
                  : isRtl
                    ? width -
                      padding.right -
                      ratio *
                        chartWidth
                    : padding.left +
                      ratio *
                        chartWidth;

              const y =
                padding.top +
                chartHeight -
                (Number(
                  point.value || 0,
                ) /
                  yMax) *
                  chartHeight;

              return {
                ...point,
                x,
                y,
              };
            },
          ),
      }),
    );

  const yTicks =
    Array.from(
      {
        length: 5,
      },
      (
        _,
        index,
      ) => {
        const ratio =
          index / 4;

        return {
          y:
            padding.top +
            chartHeight *
              ratio,

          value:
            Math.round(
              yMax *
                (1 -
                  ratio),
            ),
        };
      },
    );

  const firstPoints =
    preparedSeries[0]
      ?.points || [];

  const xLabelIndexes =
    Array.from(
      new Set([
        0,
        Math.floor(
          (firstPoints.length -
            1) /
            3,
        ),
        Math.floor(
          ((firstPoints.length -
            1) *
            2) /
            3,
        ),
        firstPoints.length - 1,
      ]),
    ).filter(
      (index) =>
        index >= 0 &&
        index <
          firstPoints.length,
    );

  const formatDate =
    (value) => {
      if (!value) {
        return '';
      }

      try {
        return new Intl.DateTimeFormat(
          language,
          {
            month: 'short',
            day: 'numeric',
          },
        ).format(
          new Date(value),
        );
      } catch {
        return value;
      }
    };

  const total =
    visibleSeries.reduce(
      (
        sum,
        item,
      ) =>
        sum +
        Number(
          item.total || 0,
        ),
      0,
    );

  if (!visibleSeries.length) {
    return (
      <div className="menu-analytics-trend-empty">
        <LineChart size={32} />

        <strong>
          {label}
        </strong>
      </div>
    );
  }

  return (
    <div className="menu-analytics-trend-chart">
      <div className="menu-analytics-trend-chart__top">
        <div>
          <span>
            {label}
          </span>

          <strong>
            {formatNumber(
              total,
              language,
            )}
          </strong>
        </div>

        {selectedId ===
        ALL_TREND_ITEMS_VALUE ? (
          <small>
            {allLabel}
          </small>
        ) : null}
      </div>

      <div className="menu-analytics-trend-chart__legend">
        {preparedSeries.map(
          (item) => (
            <span
              key={item.id}
            >
              <i
                style={{
                  backgroundColor:
                    item.color,
                }}
              />

              {item.name}

              <strong>
                {formatNumber(
                  item.total,
                  language,
                )}
              </strong>
            </span>
          ),
        )}
      </div>

      <div className="menu-analytics-trend-chart__canvas">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={label}
          preserveAspectRatio="none"
        >
          {yTicks.map(
            (tick) => (
              <g
                key={`tick-${tick.y}`}
              >
                <line
                  x1={
                    padding.left
                  }
                  y1={tick.y}
                  x2={
                    width -
                    padding.right
                  }
                  y2={tick.y}
                  className="menu-analytics-trend-chart__grid"
                />

                <text
                  x={
                    isRtl
                      ? width -
                        padding.right +
                        12
                      : padding.left -
                        12
                  }
                  y={
                    tick.y +
                    4
                  }
                  textAnchor={
                    isRtl
                      ? 'start'
                      : 'end'
                  }
                  className="menu-analytics-trend-chart__axis-label"
                >
                  {formatNumber(
                    tick.value,
                    language,
                  )}
                </text>
              </g>
            ),
          )}

          {preparedSeries.map(
            (item) => (
              <g key={item.id}>
                <path
                  d={buildSmoothPath(
                    item.coords,
                  )}
                  fill="none"
                  stroke={
                    item.color
                  }
                  strokeWidth={
                    selectedId ===
                    ALL_TREND_ITEMS_VALUE
                      ? 2.25
                      : 4
                  }
                  strokeOpacity={
                    selectedId ===
                    ALL_TREND_ITEMS_VALUE
                      ? 0.78
                      : 1
                  }
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="menu-analytics-trend-chart__line"
                />

                {selectedId !==
                ALL_TREND_ITEMS_VALUE
                  ? item.coords.map(
                      (
                        point,
                        index,
                      ) => (
                        <circle
                          key={`${item.id}-${point.date || index}`}
                          cx={
                            point.x
                          }
                          cy={
                            point.y
                          }
                          r="4"
                          fill={
                            item.color
                          }
                          stroke="#ffffff"
                          strokeWidth="2"
                          className="menu-analytics-trend-chart__point"
                        />
                      ),
                    )
                  : null}

                {selectedId !==
                  ALL_TREND_ITEMS_VALUE &&
                item.coords.length ? (
                  <>
                    <circle
                      cx={
                        item.coords[
                          item.coords.length -
                            1
                        ].x
                      }
                      cy={
                        item.coords[
                          item.coords.length -
                            1
                        ].y
                      }
                      r="8"
                      fill={
                        item.color
                      }
                      opacity="0.14"
                      className="menu-analytics-trend-chart__latest-halo"
                    />

                    <circle
                      cx={
                        item.coords[
                          item.coords.length -
                            1
                        ].x
                      }
                      cy={
                        item.coords[
                          item.coords.length -
                            1
                        ].y
                      }
                      r="4.5"
                      fill={
                        item.color
                      }
                      stroke="#ffffff"
                      strokeWidth="3"
                      className="menu-analytics-trend-chart__latest-point"
                    />
                  </>
                ) : null}
              </g>
            ),
          )}

          {xLabelIndexes.map(
            (index) => {
              const point =
                preparedSeries[0]
                  ?.coords?.[
                  index
                ];

              if (!point) {
                return null;
              }

              return (
                <text
                  key={`x-${index}`}
                  x={point.x}
                  y={
                    height -
                    15
                  }
                  textAnchor="middle"
                  className="menu-analytics-trend-chart__axis-label menu-analytics-trend-chart__axis-label--x"
                >
                  {formatDate(
                    point.date,
                  )}
                </text>
              );
            },
          )}
        </svg>
      </div>
    </div>
  );
}

function MenuAnalytics() {
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const [analytics, setAnalytics] = useState(null);
  const [menus, setMenus] = useState([]);
  const [menusLoading, setMenusLoading] = useState(true);

  const [selectedMenuId, setSelectedMenuId] = useState(
    localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || ALL_MENUS_VALUE,
  );

  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
  });

  const [loading, setLoading] = useState(true);

  const [trendScope, setTrendScope] =
    useState(TREND_SCOPE_PRODUCTS);

  const [selectedTrendItemId, setSelectedTrendItemId] =
    useState(ALL_TREND_ITEMS_VALUE);

  const establishmentId =
    localStorage.getItem(ESTABLISHMENT_STORAGE_KEY) ||
    DEFAULT_ESTABLISHMENT_ID;

  const selectedMenu = useMemo(
    () =>
      menus.find(
        (menu) =>
          String(menu.id) ===
          String(selectedMenuId),
      ) || null,
    [menus, selectedMenuId],
  );

  const allMenusSelected =
    selectedMenuId === ALL_MENUS_VALUE;

  const summary = analytics?.summary || {};
  const products = analytics?.products || [];
  const categories = analytics?.categories || [];
  const menuComparison =
    analytics?.menus ||
    analytics?.menu_comparison ||
    analytics?.menu_comparisons ||
    [];


  const productTrendSeries =
    analytics?.trends?.products ||
    [];

  const categoryTrendSeries =
    analytics?.trends?.categories ||
    [];

  const activeTrendSeries =
    trendScope ===
    TREND_SCOPE_PRODUCTS
      ? productTrendSeries
      : categoryTrendSeries;

  const currency =
    summary.currency ||
    analytics?.currency ||
    'MAD';

  const mostVisitedProduct = useMemo(() => {
    return [...products].sort(
      (firstProduct, secondProduct) =>
        Number(secondProduct.visits_count || 0) -
        Number(firstProduct.visits_count || 0),
    )[0];
  }, [products]);

  const bestSellingProduct = useMemo(() => {
    return [...products].sort(
      (firstProduct, secondProduct) =>
        Number(secondProduct.quantity_sold || 0) -
        Number(firstProduct.quantity_sold || 0),
    )[0];
  }, [products]);


  useEffect(() => {
    setSelectedTrendItemId(
      ALL_TREND_ITEMS_VALUE,
    );
  }, [
    trendScope,
    selectedMenuId,
  ]);

  useEffect(() => {
    if (
      selectedTrendItemId ===
      ALL_TREND_ITEMS_VALUE
    ) {
      return;
    }

    const stillExists =
      activeTrendSeries.some(
        (item) =>
          String(item.id) ===
          String(selectedTrendItemId),
      );

    if (!stillExists) {
      setSelectedTrendItemId(
        ALL_TREND_ITEMS_VALUE,
      );
    }
  }, [
    activeTrendSeries,
    selectedTrendItemId,
  ]);

  const fetchMenus = async () => {
    try {
      setMenusLoading(true);

      const response =
        await menusApi.getMenus(
          establishmentId,
          {
            per_page: 100,
            locale: language,
          },
        );

      const responseMenus =
        response.data?.data?.menus ||
        response.data?.data ||
        [];

      const normalizedMenus =
        Array.isArray(responseMenus)
          ? responseMenus
          : [];

      setMenus(normalizedMenus);

      const storedMenuId =
        localStorage.getItem(
          SELECTED_MENU_STORAGE_KEY,
        );

      if (!storedMenuId) {
        setSelectedMenuId(
          ALL_MENUS_VALUE,
        );
        return;
      }

      const storedMenuExists =
        normalizedMenus.some(
          (menu) =>
            String(menu.id) ===
            String(storedMenuId),
        );

      if (storedMenuExists) {
        setSelectedMenuId(
          String(storedMenuId),
        );
        return;
      }

      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );

      setSelectedMenuId(
        ALL_MENUS_VALUE,
      );
    } catch (error) {
      setMenus([]);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message:
          getApiErrorMessage(
            error,
          ),
      });
    } finally {
      setMenusLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const response =
        await analyticsApi.getMenuAnalytics(
          establishmentId,
          {
            menu_id:
              allMenusSelected
                ? undefined
                : selectedMenuId,

            date_from:
              filters.date_from ||
              undefined,

            date_to:
              filters.date_to ||
              undefined,

            locale:
              language,
          },
        );

      setAnalytics(
        response.data?.data ||
        null,
      );
    } catch (error) {
      setAnalytics(null);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message:
          getApiErrorMessage(
            error,
          ),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [establishmentId, language]);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedMenuId, language]);

  const handleMenuChange = (
    menuId,
  ) => {

    setSelectedMenuId(
      menuId,
    );

    if (
      menuId &&
      menuId !==
        ALL_MENUS_VALUE
    ) {
      localStorage.setItem(
        SELECTED_MENU_STORAGE_KEY,
        menuId,
      );
    } else {
      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );
    }
  };

  const submitFilters = (
    event,
  ) => {
    event.preventDefault();

    fetchAnalytics();
  };

  const resetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
    });

    window.setTimeout(() => {
      fetchAnalytics();
    }, 0);
  };

  if (
    loading &&
    menusLoading
  ) {
    return (
      <main className="menu-analytics-page" dir={direction}>
        <div className="menu-analytics-loading">
          <Loader2 size={32} />
          <span>
            {t(
              'menuAnalytics.loading',
            )}
          </span>
        </div>
      </main>
    );
  }

  return (
    <main className="menu-analytics-page" dir={direction}>
      <section className="menu-analytics-heading">
        <div>
          <p>
            {t(
              'menuAnalytics.eyebrow',
            )}
          </p>

          <h1>
            {t(
              'menuAnalytics.title',
            )}
          </h1>

          <span>
            {t(
              'menuAnalytics.description',
            )}
          </span>
        </div>

        <div className="menu-analytics-heading__icon">
          <BarChart3 size={30} />
        </div>
      </section>

      <section className="menu-analytics-menu-context">
        <div className="menu-analytics-menu-context__icon">
          {allMenusSelected ? (
            <Layers3 size={22} />
          ) : (
            <BookOpen size={22} />
          )}
        </div>

        <div className="menu-analytics-menu-context__content">
          <span>
            {t('menus.title')}
          </span>

          <strong>
            {allMenusSelected
              ? translateFallback(
                  t,
                  'menuAnalytics.filters.allMenus',
                  'All menus',
                )
              : getTranslatedField(
                  selectedMenu,
                  'name',
                  language,
                  'fr',
                ) ||
                '—'}
          </strong>

          <small>
            {allMenusSelected
              ? translateFallback(
                  t,
                  'menuAnalytics.filters.allMenusDescription',
                  'Combined analytics for all menus in this establishment.',
                )
              : getTranslatedField(
                  selectedMenu,
                  'description',
                  language,
                  'fr',
                ) ||
                translateFallback(
                  t,
                  'menuAnalytics.filters.selectedMenuDescription',
                  'Analytics are filtered to this menu.',
                )}
          </small>
        </div>

        <CustomSelect
          value={
            selectedMenuId
          }
          onChange={
            handleMenuChange
          }
          disabled={
            menusLoading
          }
          ariaLabel={t(
            'menus.title',
          )}
          className="menu-analytics-menu-context__select"
          options={[
            {
              value:
                ALL_MENUS_VALUE,

              label:
                translateFallback(
                  t,
                  'menuAnalytics.filters.allMenus',
                  'All menus',
                ),
            },

            ...menus.map(
              (menu) => ({
                value:
                  String(
                    menu.id,
                  ),

                label:
                  `${getTranslatedField(
                    menu,
                    'name',
                    language,
                    'fr',
                  ) || '—'}${
                    menu.is_default
                      ? ` • ${t(
                          'menus.status.default',
                        )}`
                      : ''
                  }`,
              }),
            ),
          ]}
        />
      </section>

      <form
        className="menu-analytics-filters"
        onSubmit={
          submitFilters
        }
      >
        <CustomDatePicker
          label={t(
            'menuAnalytics.filters.dateFrom',
          )}
          placeholder={translateFallback(
            t,
            'menuAnalytics.filters.selectDate',
            'Select date',
          )}
          t={t}
          language={language}
          value={
            filters.date_from
          }
          max={
            filters.date_to ||
            undefined
          }
          onChange={(
            value,
          ) =>
            setFilters(
              (
                currentFilters,
              ) => ({
                ...currentFilters,
                date_from:
                  value,
              }),
            )
          }
        />

        <CustomDatePicker
          label={t(
            'menuAnalytics.filters.dateTo',
          )}
          placeholder={translateFallback(
            t,
            'menuAnalytics.filters.selectDate',
            'Select date',
          )}
          t={t}
          language={language}
          value={
            filters.date_to
          }
          min={
            filters.date_from ||
            undefined
          }
          onChange={(
            value,
          ) =>
            setFilters(
              (
                currentFilters,
              ) => ({
                ...currentFilters,
                date_to:
                  value,
              }),
            )
          }
        />

        <button
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <Loader2
              size={17}
              className="menu-analytics-button-spinner"
            />
          ) : (
            <BarChart3
              size={17}
            />
          )}

          {t(
            'menuAnalytics.filters.apply',
          )}
        </button>

        <button
          type="button"
          onClick={
            resetFilters
          }
          disabled={loading}
        >
          <RefreshCw
            size={17}
          />

          {t(
            'menuAnalytics.filters.reset',
          )}
        </button>
      </form>

      <section className="menu-analytics-summary">
        <article>
          <span>
            <BarChart3
              size={21}
            />
          </span>

          <div>
            <strong>
              {formatNumber(
                summary.menu_visits || 0,
                language,
              )}
            </strong>

            <p>
              {t(
                'menuAnalytics.summary.menuVisits',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <ShoppingBag
              size={21}
            />
          </span>

          <div>
            <strong>
              {formatNumber(
                summary.orders_count || 0,
                language,
              )}
            </strong>

            <p>
              {t(
                'menuAnalytics.summary.orders',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <Loader2
              size={21}
            />
          </span>

          <div>
            <strong>
              {formatNumber(
                summary.pending_orders_count || 0,
                language,
              )}
            </strong>

            <p>
              {t(
                'menuAnalytics.summary.pendingOrders',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <UtensilsCrossed
              size={21}
            />
          </span>

          <div>
            <strong>
              {formatAmount(
                summary.revenue,
                currency,
                language,
              )}
            </strong>

            <p>
              {t(
                'menuAnalytics.summary.revenue',
              )}
            </p>
          </div>
        </article>
      </section>


      <section className="menu-analytics-panel menu-analytics-panel--trends">
        <div className="menu-analytics-trends-head">
          <div className="menu-analytics-panel__head">
            <span>
              <TrendingUp size={19} />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  'menuAnalytics.trends.title',
                  'View trends',
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  'menuAnalytics.trends.description',
                  'Compare daily views, then select one product or category for a focused graph.',
                )}
              </p>
            </div>
          </div>

          <div className="menu-analytics-trends-controls">
            <div className="menu-analytics-trends-tabs">
              <button
                type="button"
                className={
                  trendScope ===
                  TREND_SCOPE_PRODUCTS
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  setTrendScope(
                    TREND_SCOPE_PRODUCTS,
                  )
                }
              >
                <UtensilsCrossed
                  size={15}
                />

                {t(
                  'menuAnalytics.productsTitle',
                )}
              </button>

              <button
                type="button"
                className={
                  trendScope ===
                  TREND_SCOPE_CATEGORIES
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  setTrendScope(
                    TREND_SCOPE_CATEGORIES,
                  )
                }
              >
                <FolderTree
                  size={15}
                />

                {t(
                  'menuAnalytics.categoriesTitle',
                )}
              </button>
            </div>

            <CustomSelect
              value={
                selectedTrendItemId
              }
              onChange={
                setSelectedTrendItemId
              }
              className="menu-analytics-trends-select"
              options={[
                {
                  value:
                    ALL_TREND_ITEMS_VALUE,

                  label:
                    trendScope ===
                    TREND_SCOPE_PRODUCTS
                      ? translateFallback(
                          t,
                          'menuAnalytics.trends.allProducts',
                          'All products',
                        )
                      : translateFallback(
                          t,
                          'menuAnalytics.trends.allCategories',
                          'All categories',
                        ),
                },

                ...activeTrendSeries.map(
                  (item) => ({
                    value:
                      String(
                        item.id,
                      ),

                    label:
                      item.name,
                  }),
                ),
              ]}
            />
          </div>
        </div>

        <MultiSeriesTrendChart
          series={
            activeTrendSeries
          }
          selectedId={
            selectedTrendItemId
          }
          language={
            language
          }
          label={
            trendScope ===
            TREND_SCOPE_PRODUCTS
              ? translateFallback(
                  t,
                  'menuAnalytics.trends.productViews',
                  'Product view trend',
                )
              : translateFallback(
                  t,
                  'menuAnalytics.trends.categoryViews',
                  'Category view trend',
                )
          }
          allLabel={translateFallback(
            t,
            'menuAnalytics.trends.topSeriesHint',
            'Showing the top 4 series to keep the graph readable.',
          )}
        />
      </section>

      <section className="menu-analytics-highlights">
        <article>
          <span>
            <UtensilsCrossed
              size={21}
            />
          </span>

          <div>
            <p>
              {t(
                'menuAnalytics.highlights.mostVisitedProduct',
              )}
            </p>

            <strong>
              {mostVisitedProduct
                ?.product_name ||
                t(
                  'menuAnalytics.emptyValue',
                )}
            </strong>

            <small>
              {formatNumber(
                mostVisitedProduct
                  ?.visits_count ||
                  0,
                language,
              )}{' '}
              {t(
                'menuAnalytics.table.visits',
              )}
            </small>
          </div>
        </article>

        <article>
          <span>
            <ShoppingBag
              size={21}
            />
          </span>

          <div>
            <p>
              {t(
                'menuAnalytics.highlights.bestSellingProduct',
              )}
            </p>

            <strong>
              {bestSellingProduct
                ?.product_name ||
                t(
                  'menuAnalytics.emptyValue',
                )}
            </strong>

            <small>
              {formatNumber(
                bestSellingProduct
                  ?.quantity_sold ||
                  0,
                language,
              )}{' '}
              {t(
                'menuAnalytics.table.quantity',
              )}
            </small>
          </div>
        </article>
      </section>

      {allMenusSelected &&
      menuComparison.length >
        0 ? (
        <section className="menu-analytics-panel menu-analytics-panel--comparison">
          <div className="menu-analytics-panel__head">
            <span>
              <Layers3
                size={19}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  'menuAnalytics.menuComparisonTitle',
                  'Menu comparison',
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  'menuAnalytics.menuComparisonDescription',
                  'Compare visits, orders and revenue between menus.',
                )}
              </p>
            </div>
          </div>

          <div className="menu-analytics-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>
                    {t(
                      'menus.title',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.visits',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.orders',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.quantity',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.revenue',
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {menuComparison.map(
                  (item) => (
                    <tr
                      key={
                        item.menu_id ||
                        item.id
                      }
                    >
                      <td>
                        <strong>
                          {item.menu_name ||
                            item.name ||
                            `#${item.menu_id || item.id}`}
                        </strong>
                      </td>

                      <td>
                        {formatNumber(
                          item.visits_count ??
                            item.menu_visits ??
                            0,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.orders_count ?? 0,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.quantity_sold ?? 0,
                          language,
                        )}
                      </td>

                      <td>
                        {formatAmount(
                          item.revenue,
                          item.currency ||
                            currency,
                          language,
                        )}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="menu-analytics-grid">
        <article className="menu-analytics-panel">
          <div className="menu-analytics-panel__head">
            <span>
              <UtensilsCrossed
                size={19}
              />
            </span>

            <div>
              <h2>
                {t(
                  'menuAnalytics.productsTitle',
                )}
              </h2>

              <p>
                {t(
                  'menuAnalytics.productsDescription',
                )}
              </p>
            </div>
          </div>

          <div className="menu-analytics-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>
                    {t(
                      'menuAnalytics.table.product',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.visits',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.orders',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.quantity',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.revenue',
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {products.map(
                  (item) => (
                    <tr
                      key={
                        item.product_id
                      }
                    >
                      <td>
                        {
                          item.product_name
                        }
                      </td>

                      <td>
                        {formatNumber(
                          item.visits_count,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.orders_count,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.quantity_sold,
                          language,
                        )}
                      </td>

                      <td>
                        {formatAmount(
                          item.revenue,
                          currency,
                          language,
                        )}
                      </td>
                    </tr>
                  ),
                )}

                {!products.length && (
                  <tr>
                    <td
                      colSpan="5"
                      className="menu-analytics-empty"
                    >
                      {t(
                        'menuAnalytics.noProducts',
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="menu-analytics-panel">
          <div className="menu-analytics-panel__head">
            <span>
              <FolderTree
                size={19}
              />
            </span>

            <div>
              <h2>
                {t(
                  'menuAnalytics.categoriesTitle',
                )}
              </h2>

              <p>
                {t(
                  'menuAnalytics.categoriesDescription',
                )}
              </p>
            </div>
          </div>

          <div className="menu-analytics-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>
                    {t(
                      'menuAnalytics.table.category',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.visits',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.orders',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.quantity',
                    )}
                  </th>

                  <th>
                    {t(
                      'menuAnalytics.table.revenue',
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {categories.map(
                  (item) => (
                    <tr
                      key={
                        item.category_id
                      }
                    >
                      <td>
                        {
                          item.category_name
                        }
                      </td>

                      <td>
                        {formatNumber(
                          item.visits_count,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.orders_count,
                          language,
                        )}
                      </td>

                      <td>
                        {formatNumber(
                          item.quantity_sold,
                          language,
                        )}
                      </td>

                      <td>
                        {formatAmount(
                          item.revenue,
                          currency,
                          language,
                        )}
                      </td>
                    </tr>
                  ),
                )}

                {!categories.length && (
                  <tr>
                    <td
                      colSpan="5"
                      className="menu-analytics-empty"
                    >
                      {t(
                        'menuAnalytics.noCategories',
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </main>
  );
}

export default MenuAnalytics;